import React from "react";
import { FieldError } from "react-hook-form";

interface FormErrorMessageProps {
  error?: FieldError | any;
  className?: string;
}

/**
 * FormErrorMessage — single source of truth for inline validation error display.
 *
 * Usage:
 *   <FormErrorMessage error={errors.fieldName} />
 *
 * Works with any react-hook-form FieldError or any object with a `message` string.
 * Handles multi-line messages separated by "\n".
 */
const FormErrorMessage: React.FC<FormErrorMessageProps> = ({ error, className = "" }) => {
  if (!error) return null;

  const message: string = error?.message || "";

  if (!message) return null;

  if (message.includes("\n")) {
    return (
      <>
        {message.split("\n").map((line, index) => (
          <span
            key={index}
            className={`block text-red-500 text-xs font-light pl-1 mt-0.5 ${className}`}
          >
            {line}
          </span>
        ))}
      </>
    );
  }

  return (
    <span className={`block text-red-500 text-xs font-light pl-1 mt-0.5 ${className}`}>
      {message}
    </span>
  );
};

export default FormErrorMessage;

/**
 * Returns the correct border Tailwind class for a form input based on error state.
 *
 * Usage (for raw <input> elements not using the shared Input component):
 *   className={`... ${inputBorderClass(errors.fieldName)}`}
 */
export const inputBorderClass = (error?: FieldError | any): string =>
  error ? "border-red-500" : "border-[#D8D8D8]";

/**
 * Returns the full base class string for a rounded-full pill input.
 * Drop-in for raw <input> elements across forms.
 *
 * Usage:
 *   className={pillInputClass(errors.fieldName)}
 */
export const pillInputClass = (error?: FieldError | any): string =>
  `w-full rounded-full py-[6px] px-4 outline-none bg-white border text-[#1E1E1E] ${
    error ? "border-red-500" : "border-[#D8D8D8]"
  }`;
